import { posthog } from "posthog-js";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { useProfileQuery } from "@/queries/user/profile";

import StockImage from "@/assets/preprocess_clip.jpeg";

import { PrescreenLayout } from "@/components/prescreen";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const Preprocess = () => {
  const [selected, setSelected] = useState<"candidate" | "company" | "">("");

  const { profile, profileLoading } = useProfileQuery();

  const { isLoading, isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && profile?.userType.length) {
      navigate("/landing");
    }
  }, [isAuthenticated, profile, navigate]);

  const handleLogin = () =>
    void (async () => {
      await loginWithRedirect({
        appState: {
          returnTo: "/landing",
        },
      });
    })();

  const handleLogout = () => {
    void logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const handleNext = () => {
    posthog.capture("lets_get_started");

    return navigate(
      selected === "candidate"
        ? "/pre-screen/candidate/two"
        : "/pre-screen/company/two",
    );
  };

  if (
    isLoading ||
    (isAuthenticated && (profileLoading || profile?.userType.length))
  ) {
    return;
  }

  return (
    <PrescreenLayout
      title={"I’m here because I’m a..."}
      form={
        <>
          <div className="flex flex-col gap-4 md:pb-0">
            <RadioGroup
              value={selected}
              onValueChange={(data) =>
                setSelected(data as "candidate" | "company")
              }
            >
              <RadioGroupItem
                variant="radioBar"
                label="Professional ready to work"
                description="Get connected with startups looking for experienced leaders"
                value="candidate"
              />
              <RadioGroupItem
                variant="radioBar"
                label="Startup looking for help"
                description="Skip the hiring hassle and find a leader on demand"
                value="company"
              />
            </RadioGroup>
            <div className="flex gap-4 items-top justify-between self-stretch">
              <p className="text-base font-normal text-[#2e2e38]">
                {isAuthenticated ? (
                  <>
                    Signed in as {profile?.name ?? profile?.emails[0]}.{" "}
                    <span
                      className="text-base font-semibold text-inline-link hover:underline cursor-pointer"
                      onClick={handleLogout}
                    >
                      Sign out
                    </span>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <span
                      className="text-base font-semibold text-inline-link hover:underline cursor-pointer"
                      onClick={handleLogin}
                    >
                      Sign in
                    </span>
                  </>
                )}
              </p>
              <Button
                variant="success"
                disabled={!selected}
                onClick={handleNext}
              >
                Continue
              </Button>
            </div>
          </div>
        </>
      }
      infoCard={{
        background: StockImage,
        title: (
          <>
            The{" "}
            <b>
              <i>#1 platform</i>
            </b>{" "}
            for Fractional Executives
          </>
        ),
        subtitle:
          "We help startups grow faster by connecting them with top-tier talent.",
        footer: true,
        mobileFragments: true,
      }}
    />
  );
};

export default Preprocess;
